import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  APIActionOnReservationByID,
  ReservationActions,
} from "utils/api";
import "./AssessmentInstancePage.scss";
import "../ErrorLoginPage/ErrorLoginPage.scss";
import LoadingImage from "components/LoadingImage";
import { useDebugMode } from "components/DebugContext/DebugContext";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import renderers from "utils/markdown";
import { useTranslation } from "react-i18next";
import EventsList from "views/LabInstancePage/components/EventsList";
import AssessmentGoalsList from "./components/AssessmentGoalsList";
import CongratulateAssessmentModal from "./components/CongratulateAssessmentModal";
import { useMutation } from "@tanstack/react-query";
import { useAssignmentsContext } from "components/AssignmentsProvider";
import TimeoutAssessmentModal from "./components/TimeoutAssessmentModal";
import { useInstanceRunning } from "components/InstanceRunningContext/InstanceRunningContext";

export default function AssessmentInstancePage() {
  const { debugMode } = useDebugMode();
  const { t } = useTranslation();
  const { lab, reservation, refreshReservation, refreshAssessmentInvitation, refreshInstance, instance, isInstanceLoaded, isInstanceError, instanceError, isInstanceLoading, isInstanceFetching }: any = useAssignmentsContext();
  const { showTimeoutModal, setInstanceDetails } = useInstanceRunning();
  const [goals, setGoals] = useState([]);
  const [finishLabError, setFinishLabError] = useState("");

  const [openCongratulateModal, setOpenCongratulateModal] = useState(false);
  const handleOpenCongratulateModal = () => {
    setOpenCongratulateModal(true);
    setInstanceDetails(null, 0, 0, false, () => {});
  };

  // finish lab
  const finishLabMutation = useMutation({
    mutationFn: () => APIActionOnReservationByID(reservation.id, ReservationActions.End),
  });

  useEffect(() => {
    // refresh instance on mount
    refreshInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Create goals from resources
   */
  useEffect(() => {
    if (instance) {
      console.log("instance in assessment instance page", instance);
      let parsed_goals = instance.resources.map((g) => {
        if (g.value && typeof g.value === "string")
          g.value = JSON.parse(g.value);
        return g;
      });
      setGoals(parsed_goals);

      // let durationMs = instance.lab ? (instance.lab.duration * 60 * 1000) : (instance.duration * 60 * 1000);
      let durationMs = Date.parse(reservation.end_date) - Date.parse(instance.updated_at);
      setInstanceDetails(instance.id, instance.updated_at, durationMs, true, () => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, isInstanceLoaded]);

  const handleInstanceAction = async (e, action) => {
    e.preventDefault();
    if (instance) {
      if (action === ReservationActions.End) {
        try {
          await finishLabMutation.mutateAsync();
          handleOpenCongratulateModal();
          refreshReservation();
          refreshAssessmentInvitation();
        } catch (err) {
          setFinishLabError(err.message);
        }
      } else {
        handleOpenCongratulateModal();
      }
    }
  };

  // the below logic causes an infinite loop. No need to refresh the invitation and reservation when the timeout modal is open
  // useEffect(() => {
  //   if (showTimeoutModal) {
  //     refreshAssessmentInvitation();
  //     refreshReservation();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [showTimeoutModal]);

  if (isInstanceError) {
    return (
      <div className="assessment-instance-page">Error: {instanceError}</div>
    );
  }
  if (!isInstanceLoaded || isInstanceLoading) {
    return (
      <div className="assessment-instance-page">
        <LoadingImage></LoadingImage>
      </div>
    );
  }
  return (
    <>
      <CongratulateAssessmentModal
        open={openCongratulateModal}
        handleClose={() => { }}
      ></CongratulateAssessmentModal>

      {showTimeoutModal && (
        <TimeoutAssessmentModal
          open={true}
          handleClose={() => { }}
        />
      )}

      <main className="assessment-instance-page">
        <section className="assessment-instance-page-header">
          <h1 className="title">{lab && lab.name}</h1>
          <h4 className="section_title section">Description:</h4>
          <div className="description">
            <ReactMarkdown
              components={renderers}
              rehypePlugins={[rehypeHighlight]}
              remarkPlugins={[remarkGfm]}
              className="prose prose-base max-w-none !font-poppins"
            >
              {lab?.description}
            </ReactMarkdown>
          </div>
        </section>

        <section className="goals-detailed">
          {isInstanceFetching || isInstanceLoading ? (
            <LoadingImage className="!h-[53px] !w-[171px]" />
          ) : (
            (goals && goals.length > 0 ? (
              <>
                <h4 className="section_title section">{t("goals-detailed")}</h4>
                <AssessmentGoalsList
                  instance={instance}
                  goals={goals}
                ></AssessmentGoalsList>
                <div className="instance-finish-helper-text">
                  {t("great-job-so-far-you-can-finish-your-test")}{" "}
                </div>
                <Grid
                  className="instance-finish-container"
                  justifyContent={"end"}
                  container
                  gap={"15px"}
                  marginRight={"5px"}
                >
                  <FormControl>
                    {finishLabMutation.isPending && (
                      <LoadingImage className="!h-[53px] !w-[171px]" />
                    )}
                    {finishLabMutation.isIdle && (
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          setFinishLabError("");
                          handleInstanceAction(e, ReservationActions.End);
                        }}
                        disabled={!instance}
                        className="instance-finish-btn !mx-auto hover:!bg-blue-dark"
                        variant="contained"
                      >
                        {t("finish")}{" "}
                      </Button>
                    )}
                    <FormHelperText
                      className="instance-finish-btn-error"
                      style={{ color: "#EF4444" }}
                    >
                      {finishLabError}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {debugMode && (
                  <div className="relative mt-4 rounded border-4 border-dotted border-red">
                    <span className="absolute right-4 text-xs font-thin text-red">
                      debug mode
                    </span>
                    <section className="events">
                      <h4 className="section_title section">Events:</h4>
                      <EventsList></EventsList>
                    </section>
                  </div>
                )}
              </>
            ) : (
              <div>{t("no-goals-found-for-this-instance")}</div>
            )))}
        </section>
      </main>
    </>
  );
}
